import {computed, inject, ref} from 'vue';
import {defineStore, storeToRefs} from 'pinia';
import {useLogger, useTranslation} from '#imports';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import {Configuration, RecommendationsApi} from '@api';
import {RegisteredRepositories} from '~/config/application.services';
import {useProfileStore} from '~/store/profileStore';
import type {RecommendationRequest} from '~/types/domain/RecommendationRequest';
import type {Recommendation, Recommendations} from '~/types/domain/Recommendations';

export const useRecommendationsStore = defineStore('recommendations', () => {
    const _profileRepository = inject(RegisteredRepositories.injectionKeys.profile)!;
    const _recommendationRepository = inject(RegisteredRepositories.injectionKeys.recommendation)!;
    const _recommendationApi = new RecommendationsApi(new Configuration({basePath: ''}));

    // composables
    const logger = useLogger();
    const {t} = useTranslation();

    // stores
    const {profile} = storeToRefs(useProfileStore());

    // refs a-z
    const recommendations = ref<Recommendations>();

    // computed values
    const recommendationsAccepted = computed((): Recommendation[] => recommendations.value?.recommendations.filter((reco) => reco.accepted) || []);
    const recommendationsAcceptedWithTextContent = computed(
        (): Recommendation[] => recommendationsAccepted.value.filter((reco) => reco.textContent && reco.textContent.trim().length > 0) || [],
    );

    async function deleteRecommendation(recommendation: Recommendation) {
        await _recommendationRepository.deleteRecommendation(recommendation);
        await getRecommendations();
    }

    async function getRecommendations(): Promise<Recommendations | undefined> {
        try {
            recommendations.value = await _profileRepository.fetchRecommendations(profile.value!);
        } catch (error: unknown) {
            logger.error(error);
            throw error;
        }
        return recommendations.value;
    }

    async function sendRecommendation(recommendationRequest: RecommendationRequest): Promise<void> {
        await _profileRepository.sendRecommendation(recommendations.value!, recommendationRequest);
        await getRecommendations();
    }

    function setRecommendations(newRecommendations: Recommendations | undefined) {
        recommendations.value = newRecommendations;
    }

    async function toggleRecommendationVisibility(recommendation: Recommendation) {
        try {
            const hidden = recommendation.hidden === undefined ? true : !recommendation.hidden;
            await _recommendationApi.patchRecommendationVisibility({recommendationId: recommendation.id, patchRecommendationRequest: {hidden}});
            await getRecommendations();
        } catch (error) {
            logger.error(error);
            pushVJoySnackbar({
                props: {
                    message: t('profile.section.recommendation.toggle.visibility.error'),
                    level: 'error',
                },
            });
        }
    }

    return {
        // refs a-z
        recommendations,
        // computed values
        recommendationsAccepted,
        recommendationsAcceptedWithTextContent,
        // functions a-z
        deleteRecommendation,
        getRecommendations,
        sendRecommendation,
        setRecommendations,
        toggleRecommendationVisibility,
    };
});
